/**
 * MessageBubble — a single message row in the chat area (Support Center).
 * Handles outgoing/incoming/system layouts + delivery indicator.
 * Extracted from Support.js during the iter-238 refactor (Feb 2026).
 */
import React from 'react';
import {
  Check, CheckCheck, Clock, XCircle,
} from 'lucide-react';
import { ChannelIcon, channelOf, formatTime } from './atoms';

export function MessageBubble({ msg, selectedContact, isAr, t }) {
  // Compute delivery indicator for outgoing messages.
  // Priority: failed → read → delivered → sent → pending.
  let TickIcon = null;
  let tickClass = '';
  let tickTitle = '';
  if (msg.direction === 'outgoing') {
    const ds = msg.delivery_status || msg.status;
    if (ds === 'failed' || msg.status === 'failed') {
      TickIcon = XCircle;
      tickClass = 'text-red-300';
      tickTitle = msg.delivery_error?.message
        ? t(`فشل: ${msg.delivery_error.message}`, `Failed: ${msg.delivery_error.message}`)
        : t('فشل التسليم', 'Delivery failed');
    } else if (ds === 'read') {
      TickIcon = CheckCheck;
      tickClass = 'text-sky-200';
      tickTitle = t('تمت القراءة', 'Read');
    } else if (ds === 'delivered') {
      TickIcon = CheckCheck;
      tickClass = 'text-white/60';
      tickTitle = t('سُلّمت', 'Delivered');
    } else if (ds === 'sent') {
      TickIcon = Check;
      tickClass = 'text-white/60';
      tickTitle = t('مُرسلة', 'Sent');
    } else {
      TickIcon = Clock;
      tickClass = 'text-white/60';
      tickTitle = t('قيد الإرسال', 'Sending');
    }
  }

  return (
    <div
      className={`flex items-end gap-2 ${
        msg.direction === 'outgoing' ? 'justify-start' :
        msg.direction === 'system' ? 'justify-center' : 'justify-end'
      }`}
    >
      {msg.direction === 'system' ? (
        <div className="bg-white/[0.04] text-[#808080] text-xs px-4 py-2 rounded-full">
          {msg.text}
        </div>
      ) : (
        <>
          {/* Meta strip (time + tick + channel) on the LEFT of the outgoing bubble */}
          {msg.direction === 'outgoing' && (
            <div className="flex flex-col items-end min-w-[52px] pb-1 text-[#71717A]">
              {TickIcon && (
                <span title={tickTitle} className={`inline-flex mb-0.5 ${tickClass}`}>
                  <TickIcon
                    data-testid={`msg-status-${msg.delivery_status || msg.status}`}
                    className="h-4 w-4"
                  />
                </span>
              )}
              <span className="text-[10px] font-mono leading-none">
                {formatTime(msg.created_at, isAr)}
              </span>
              <ChannelIcon
                channel={channelOf(msg) || channelOf(selectedContact)}
                size={10}
                className="opacity-70 mt-0.5"
              />
            </div>
          )}

          <div
            className={`max-w-[65%] px-3 py-2 rounded-2xl relative shadow-sm ${
              msg.direction === 'outgoing'
                ? (msg.status === 'failed' || msg.delivery_status === 'failed'
                    ? 'bg-red-500/25 border border-red-500/40 text-white rounded-bl-sm'
                    : 'bg-[#25D366]/95 text-white rounded-bl-sm')
                : 'bg-white/[0.04] text-white border border-white/5 rounded-br-sm'
            }`}
          >
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
            {msg.direction === 'outgoing' && msg.delivery_error?.message && (
              <p className="text-[10px] mt-1 text-red-100/90 leading-tight">
                {msg.delivery_error.message}
              </p>
            )}
          </div>

          {/* Meta strip on the right of INCOMING bubble */}
          {msg.direction === 'incoming' && (
            <div className="flex flex-col items-end pb-1 text-[#71717A]">
              <span className="text-[10px] font-mono leading-none">
                {formatTime(msg.created_at, isAr)}
              </span>
              <ChannelIcon
                channel={channelOf(msg) || channelOf(selectedContact)}
                size={10}
                className="opacity-70 mt-0.5"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
